import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface TimePickerProps {
  /** Put on the trigger button, so a `<Label htmlFor>` can name the field. */
  id?: string;
  /** 24-hour `HH:mm`, or "" for unset. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Minute granularity. Site times are recorded to the nearest five. */
  minuteStep?: number;
  clearLabel?: string;
  hoursLabel?: string;
  minutesLabel?: string;
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `"09:30"` → `{ hour: 9, minute: 30 }`; anything else is unset. */
function parse(value: string): { hour: number; minute: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/**
 * A 24-hour time picker in the app's own styling.
 *
 * `<input type="time">` was rendering the browser's native picker, which
 * follows the *browser* locale rather than the document's — so a machine set to
 * en-US offered 12/22/AM columns to people filling in Estonian site forms.
 * There is no reliable way to force 24-hour on the native control, so the
 * control is ours: two columns, `HH:mm`, no meridiem anywhere.
 */
export function TimePicker({
  id,
  value,
  onChange,
  placeholder = "--:--",
  disabled,
  className,
  minuteStep = 5,
  clearLabel = "Clear",
  hoursLabel = "Hours",
  minutesLabel = "Minutes",
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parse(value);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  const minutes = useMemo(
    () => Array.from({ length: Math.ceil(60 / minuteStep) }, (_, index) => index * minuteStep),
    [minuteStep]
  );

  // Open on the current selection rather than at midnight — 17:45 should not
  // need eighteen rows of scrolling to confirm.
  useEffect(() => {
    if (!open || !selected) return;
    for (const [ref, key] of [
      [hourListRef, selected.hour],
      [minuteListRef, selected.minute],
    ] as const) {
      ref.current?.querySelector(`[data-value="${key}"]`)?.scrollIntoView({ block: "center" });
    }
  }, [open, selected]);

  const commit = (part: "hour" | "minute", next: number) => {
    const base = selected ?? { hour: 0, minute: 0 };
    const merged = { ...base, [part]: next };
    onChange(`${pad(merged.hour)}:${pad(merged.minute)}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start gap-2 font-normal",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <Clock className="h-4 w-4" />
          {selected ? `${pad(selected.hour)}:${pad(selected.minute)}` : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex divide-x">
          <TimeColumn
            ref={hourListRef}
            label={hoursLabel}
            options={HOURS}
            selected={selected?.hour}
            onPick={(hour) => commit("hour", hour)}
          />
          <TimeColumn
            ref={minuteListRef}
            label={minutesLabel}
            options={minutes}
            selected={selected?.minute}
            onPick={(minute) => commit("minute", minute)}
          />
        </div>
        {selected && (
          <div className="flex items-center justify-end border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              {clearLabel}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface TimeColumnProps {
  label: string;
  options: number[];
  selected?: number;
  onPick: (value: number) => void;
}

const TimeColumn = forwardRef<HTMLDivElement, TimeColumnProps>(function TimeColumn(
  { label, options, selected, onPick },
  ref
) {
  return (
    <div className="flex w-16 flex-col">
      <span className="px-2 py-1.5 text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div
        ref={ref}
        className="max-h-56 overflow-y-auto px-1 pb-1"
        role="listbox"
        aria-label={label}
      >
        {options.map((option) => (
          <button
            key={option}
            type="button"
            role="option"
            aria-selected={option === selected}
            data-value={option}
            onClick={() => onPick(option)}
            className={cn(
              "w-full rounded-md py-1.5 text-center text-sm tabular-nums transition-colors",
              // The theme's own accent for the selection, so the control reads
              // as Franklin's rather than the operating system's.
              option === selected
                ? "bg-primary font-medium text-primary-foreground"
                : "hover:bg-muted"
            )}
          >
            {pad(option)}
          </button>
        ))}
      </div>
    </div>
  );
});
